/* globals data, serverParams */
import * as d3 from 'd3'

import { colorScale } from 'bam-node-baseball-savant/utils/scales'
import { appSettings } from 'bam-node-baseball-savant/leaderboard/appSettings'
import PlayerSwarm from './PlayerSwarm'
import SummarySwarm from './SummarySwarm'
import { infield, positions } from './constants'

const { year } = appSettings.armStrength

function getQualifiedThrows(player, position) {
    const totalThrows = player[`total_throws_${position}`]
    if (position === '1b') {
        return Math.round(totalThrows * 0.01)
    }
    if (infield.includes(position)) {
        return Math.round(totalThrows * 0.05)
    }
    return Math.round(totalThrows * 0.1)
}

const positionData = positions.map((position) => {
    const filteredData = data
        .filter((player) => player[`arm_${position}`])
        .map((player) => ({
            name: player.fielder_name,
            throws: player[`total_throws_${position}`],
            qualifiedThrows: getQualifiedThrows(player, position),
            arm: Number(player[`arm_${position}`]),
            max: Number(player.max_arm_strength),
            position,
            player_id: player.player_id,
        }))

    return {
        position,
        data: filteredData,
    }
})

if (document.getElementById('svg')) {
    setupViz()
}

function setupViz() {
    const svg = d3
        .select('#svg')
        // .style('background-color', 'rgb(232, 232, 232)')
        .style('background-color', '#e4f2f5')
        .style('width', '100%')
        .style('min-width', '350px')
        .attr('height', 800)

    let { height, width } = svg.node().getBoundingClientRect()

    function setMargin(svgWidth) {
        return {
            top: 120,
            right: 10,
            bottom: 0,
            left: svgWidth < 800 ? 30 : 60,
        }
    }

    let margin = setMargin(width)

    const chartHeight = height - margin.top - margin.bottom
    let chartWidth = width - margin.left - margin.right

    const dataFlat = positionData.flatMap((d) => d.data)

    const chart = svg
        .append('g')
        .attr('id', 'arm-swarm-chart')
        .attr('transform', `translate(${margin.left}, ${margin.top})`)

    function setChartPosition(chartEl, marginVal) {
        chartEl.attr(
            'transform',
            `translate(${marginVal.left}, ${marginVal.top})`
        )
    }

    setChartPosition(chart, margin)

    const chartInfo = {
        svg,
        chart,
        margin,
        chartHeight,
        chartWidth,
    }

    const defaultX = [59, 105]
    const detailedX = [20, 105]
    const xScale = d3.scaleLinear().domain(defaultX).range([0, chartWidth])
    const xScaleDetailed = d3
        .scaleLinear()
        .domain(detailedX)
        .range([0, chartWidth])

    let playerView = false
    function getRelevantScale() {
        return playerView ? xScaleDetailed : xScale
    }

    const yScale = d3
        .scaleBand()
        .domain(positions)
        .range([0, chartHeight])
        .padding(0.1)

    const scales = {
        xScale,
        xScaleDetailed,
        yScale,
        colorScale,
    }

    const dataInitial = dataFlat.map((d) => ({
        ...d,
        x: xScale(d.arm),
        y: yScale(d.position),
    }))

    // const initialValue = document.querySelector(
    //     '#arm-strength-qualified-throws'
    // ).value
    const initialValue = 0
    let filteredData = dataInitial.filter(
        (d) => d.qualifiedThrows >= initialValue
    )

    chart.append('text').attr('class', 'title')

    drawTitle()
    drawHorizontalGrid()
    drawVerticalGrid(xScale)

    const summarySwarm = new SummarySwarm(
        filteredData,
        chartInfo,
        scales,
        toggleView
    )

    function updateGraph() {
        // const minThrows = document.getElementById(
        //     'arm-strength-qualified-throws'
        // ).value
        const minThrows = 0
        const search = document.getElementById('arm-strength-search').value
        filteredData = dataInitial
            .filter((d) => d.qualifiedThrows >= minThrows)
            .filter((d) => d.name.toLowerCase().includes(search.toLowerCase()))

        summarySwarm.updatePlayerSearch(search)
    }

    document
        .querySelector('#arm-strength-search')
        .addEventListener('input', () => {
            updateGraph()
        })

    // document
    //     .querySelector('#arm-strength-qualified-throws')
    //     .addEventListener('input', (e) => {
    //         updateGraph()

    //         document.getElementById('min-throws').innerText = e.target.value
    //     })

    function drawHorizontalGrid() {
        chart
            .selectAll('g.position')
            .data(positions)
            .join(
                (enter) => {
                    const g = enter
                        .append('g')
                        .attr('class', 'position')
                        .attr('transform', (d) => `translate(0, ${yScale(d)})`)

                    g.append('text')
                        .attr(
                            'transform',
                            () => `translate(${(3 * margin.left) / -4}, 0)`
                        )
                        .text((d) => d.toUpperCase())
                        .attr('font-family', 'sans-serif')
                        .attr('font-size', 16)
                        .attr('font-weight', 'bold')
                        .attr('fill', '#333')
                        .attr('dominant-baseline', 'middle')

                    g.append('rect')
                        .attr('width', chartWidth)
                        .attr('height', 1)
                        .attr('fill', '#fff')

                    return g
                },
                (update) => {
                    update.select('rect').transition().attr('width', chartWidth)
                    update
                        .select('text')
                        .attr(
                            'transform',
                            () => `translate(${(3 * margin.left) / -4}, 0)`
                        )
                    return update
                }
            )
    }

    function drawTitle() {
        chart
            .select('text.title')
            .attr('x', chartWidth / 2)
            .attr('y', -margin.top + 30)
            .attr('text-anchor', 'middle')
            .attr('font-family', 'sans-serif')
            .attr('font-size', window.innerWidth > 700 ? 22 : 14)
            .attr('font-weight', 'bold')
            .text(
                `Which players throw hardest at each position?  (${
                    Number(serverParams.year) === 9999
                        ? `2020-${year}`
                        : serverParams.year
                })`
            )
    }

    function drawVerticalGrid(relevantXScale) {
        const [min] = relevantXScale.domain()
        const lines = [
            20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100,
        ]
        chart
            .selectAll('g.vertical-grid')
            .data(lines)
            .join(
                (enter) => {
                    const el = enter
                        .append('g')
                        .attr('class', 'vertical-grid')
                        .attr(
                            'transform',
                            (d) => `translate(${relevantXScale(d)}, -40)` // do better vertically
                        )
                    el.append('rect')
                        .attr('fill', (d) =>
                            d % 10 ? 'rgba(255,255,255,0.4)' : '#fff'
                        )
                        .attr('height', chartHeight)
                        .attr('width', 1)
                    el.append('text')
                        .text((d) => (d % 10 ? '' : `${d}`))
                        .attr('dy', -10)
                        .attr('font-family', 'sans-serif')
                        .attr('font-weight', 'bold')
                        .attr('font-size', 16)
                        .attr('fill', '#333')
                        .attr('text-anchor', 'middle')
                    el.append('text')
                        .text((d) => (d % 10 ? '' : `mph`))
                        .attr('dy', 6)
                        .attr('font-family', 'sans-serif')
                        // .attr('font-weight', 'bold')
                        .attr('fill', '#666')
                        .attr('font-size', 14)
                        .attr('text-anchor', 'middle')
                    return el
                },
                // NOTE: D3v7 seems to be okay with returning a transition, but v6 is not
                (update) => {
                    update
                        .transition()
                        .attr(
                            'transform',
                            (d) => `translate(${relevantXScale(d)}, -40)`
                        )

                    return update
                }
            )
            .attr('opacity', (d) => (d >= min ? 1 : 0))
    }

    function rescale(playerViewCopy) {
        const relevantXScale = getRelevantScale(playerViewCopy)

        drawTitle()
        drawHorizontalGrid()
        drawVerticalGrid(relevantXScale)

        summarySwarm.restartSimulation(relevantXScale)
        summarySwarm.rescaleTooltip(relevantXScale)
    }

    let swarm
    let loading = false

    const $loadingText = document.getElementById('loading-indicator')
    const $helperText = document.getElementById('helper-text')
    const $playerLegend = document.getElementById('player-bubble-legend')
    const $qualExample = document.getElementById('qualified-example')
    const $nonQualExample = document.getElementById('non-qualified-example')

    // $qualExample.style.display = 'inline-block'
    // $nonQualExample.style.display = 'inline-block'

    function setLoading() {
        loading = true
        $loadingText.style.display = 'block'
    }

    function endLoading() {
        loading = false
        $loadingText.style.display = 'none'
    }

    async function toggleView(player) {
        playerView = !playerView

        if (playerView) {
            setLoading()
            const individualData = await fetch(
                `/leaderboard/arm-strength/${player.player_id}/${serverParams.year}`
            ).then((res) => res.json())
            swarm = new PlayerSwarm(individualData, chart, player, scales)
            showLegend()
            d3.selectAll('circle.force').transition().attr('opacity', 0.08)
            endLoading()
        } else {
            if (swarm) {
                swarm.destroy()
            }
            hideLegend()
            d3.selectAll('circle.force')
                .attr('opacity', 1)
                .style('cursor', 'pointer')
        }
        rescale(playerView)
    }

    function showLegend() {
        const diff = (swarm.radius - swarm.smallRadius) * 2
        const legendMargin = 5
        $qualExample.style.height = `${swarm.radius * 2 + 2}px`
        $qualExample.style.width = `${swarm.radius * 2 + 2}px`
        $qualExample.style.marginRight = `${legendMargin}px`
        $qualExample.style.borderRadius = '50%'
        $qualExample.style.border = '1px solid #ccc'
        $qualExample.style.background = swarm.colorScale(swarm.player.position)

        $nonQualExample.style.height = `${swarm.smallRadius * 2 + 2}px`
        $nonQualExample.style.width = `${swarm.smallRadius * 2 + 2}px`
        $nonQualExample.style.marginRight = `${legendMargin + diff}px`
        $nonQualExample.style.borderRadius = '50%'
        $nonQualExample.style.border = '1px solid #666'
        $nonQualExample.style.background = '#fff'

        $playerLegend.style.display = 'block'

        $helperText.textContent = 'Click a colored bubble to view video'
    }

    function hideLegend() {
        $playerLegend.style.display = 'none'
        $helperText.textContent =
            "Click a bubble to see all the player's throws"
    }

    document.addEventListener('click', () => {
        if (loading) {
            return
        }

        if (playerView) {
            toggleView()
            if (summarySwarm) {
                summarySwarm.removeTooltips()
                const search = document.getElementById(
                    'arm-strength-search'
                ).value

                // This sucks. Instead of using a callback just guess when the element will be back in the spot we expect :(
                summarySwarm.updatePlayerSearch(search, 1000)
            }
        }
    })

    window.addEventListener('resize', () => {
        width = svg.node().getBoundingClientRect().width
        margin = setMargin(width)
        chartWidth = width - margin.left - margin.right

        svg.attr('width', width)
        setChartPosition(chart, margin)

        xScale.range([0, chartWidth])
        xScaleDetailed.range([0, chartWidth])

        rescale(playerView)
    })
}
